import { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, ButtonGroup, Modal } from 'react-bootstrap';
import PageHeader from '../../components/PageHeader';
import Swal from 'sweetalert2';
import { apiUrl } from '../../config/apiUrls';
import { useNavigate } from 'react-router-dom';
import { usePaymentStore } from '../../hooks/ai-hooks/usePaymentCredit';
import { generateSlug } from '../../config/generateDebateSlug';
import { useUserTopic } from '../../hooks/ai-hooks/useCommSkillsTopic';
import { v4 as uuidv4 } from 'uuid';
import { HR_KEYWORDS, HR_ROLE_KEYWORDS, TECH_ROLE_KEYWORDS, TECHNICAL_KEYWORDS } from '../../utils/interviewTypeDetector';
import useDebounce from '../../hooks/use-debounce';


function detectInterviewType(text = "") {
  const content = text.toLowerCase();

  let techScore = 0;
  let hrScore = 0;

  TECH_ROLE_KEYWORDS.forEach(word => {
    if (content.includes(word)) techScore += 3;
  });

  HR_ROLE_KEYWORDS.forEach(word => {
    if (content.includes(word)) hrScore += 3;
  });

  TECHNICAL_KEYWORDS.forEach(word => {
    if (content.includes(word)) techScore += 1;
  });

  HR_KEYWORDS.forEach(word => {
    if (content.includes(word)) hrScore += 1;
  });

  if (techScore > 0 && hrScore > 0) return "mixed";
  if (hrScore > techScore) return "hr";
  if (techScore > hrScore) return "technical";

  return "hr";
}

function InterviewPrep() {
  const BASE_URL = process.env.REACT_APP_AI_API_URL
  const router = useNavigate()
  const [loader, setLoader] = useState(false)
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const studentId = sessionStorage.getItem("id")
  const [isenableTech, setIsEnableTech] = useState(null)
  const [formData, setFormData] = useState({
    jobRole: "",
    jobDescription: "",
    interviewType: "technical",
    duration: "10",
    techStack: "",
    yearsOfExperience: 0,
  });

  const debouncedSearchText = useDebounce(formData.jobRole == '' ? null : formData.jobRole, 2000)

  useEffect(() => {
    if (!debouncedSearchText) {
      setIsEnableTech(null);
      return;
    }

    const detectedType = detectInterviewType(debouncedSearchText);
    setIsEnableTech(detectedType);

    if (detectedType === "hr") {
      handleChange("interviewType", "hr");
    }
    if (detectedType === "technical") {
      handleChange("interviewType", "technical");
    }
  }, [debouncedSearchText]);

  const interviewTypeLabel = {
    technical: "Technical Interviewer (coding, concepts, system design)",
    hr: "HR Interviewer (behavioral & soft skills)",
    mixed: "Panel Interview (Technical + HR)",
  };

  const setupChecklist = [
    {
      id: 1,
      title: "Microphone Access",
      desc: "Enable microphone permissions for voice interaction",
      icon: "bi-mic-fill",
      required: true
    },
    {
      id: 2,
      title: "Camera ( Optional )",
      desc: "Enable camera for non-verbal cues analysis",
      icon: "bi-camera-video-fill",
      required: false
    },
    {
      id: 3,
      title: "Stable Internet",
      desc: "Ensure 5+ Mbps connection for smooth experience",
      icon: "bi-wifi",
      required: true
    },
    {
      id: 4,
      title: "Quiet Environment",
      desc: "Choose a quiet space to minimize background noise",
      icon: "bi-volume-mute-fill",
      required: true
    },
    {
      id: 5,
      title: "Headphones ( Recommended )",
      desc: "Use headphones for better audio clarity",
      icon: "bi-headphones",
      required: false
    }
  ];

  const howItWorksSteps = [
    {
      step: "1",
      title: "Set Up Your Environment",
      desc: "Find a quiet place with stable internet. Enable your microphone for a real interview experience."
    },
    {
      step: "2",
      title: "AI Interviewer Starts",
      desc: "AI will act as a real interviewer based on your selected interview type (Technical, HR, or Mixed)."
    },
    {
      step: "3",
      title: "Answer & Get Feedback",
      desc: "Respond via voice or text. AI analyzes your answers and provides structured feedback."
    },
  ];

  const startInterview = async (payload) => {
    try {
      const response = await fetch(
        `${BASE_URL}${apiUrl.InterviewPrepStart}`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      // ✅ SUCCESS
      if (response.status === 200) {
        if (result?.paymentData) {
          usePaymentStore.getState().setPaymentData(result.paymentData);
        }

        router(`/practice-with-ai/interview/${result.uniqueId}`);
        return;
      }

      if (response.status === 402) {
        if (result?.paymentData) {
          usePaymentStore.getState().setPaymentData(result.paymentData);
        }
        Swal.fire('Insufficient Credits', "You’ve used all credits. Upgrade your plan to continue practicing and improving 🚀", 'warning')

        return;
      }

      Swal.fire(
        "Failed to Start",
        result?.message || "Please try again later",
        "error"
      );

      // router(`/practice-with-ai/interview-prep/${"uniqueId"}`);
    } catch (error) {
      console.error(error);
      Swal.fire("Failed to Start", "Network error. Please try again.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);

    const uniqueId = uuidv4();

    const payload = {
      userId: studentId,
      sessionId: uniqueId,
      jobType: formData.jobRole,
      jobDescription: formData.jobDescription,
      techStack: formData.techStack,
      yearsOfExperience: formData.yearsOfExperience,
      interviewType: formData.interviewType,
      duration: formData.duration,
      topic: generateSlug(formData.jobRole, "", formData.interviewType, formData.duration)
    };

    try {
      await startInterview({ ...payload, uniqueId });
      // setData(payload)
      useUserTopic.getState().setModuleData("interview", payload);
    } catch (error) {
      console.error(error)
    } finally {
      setLoader(false)
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <>
      <Row>
        <Col>
          <PageHeader
            title="AI Interview Preparation."
            breadcrumbs={[
              { label: "Dashboard", path: "/dashboard" },
              { label: "Practice With AI", path: '/practice-with-ai' },
              { label: "Interview Preparation.", active: true },
            ]}
          />
        </Col>
      </Row>

      <Container className="py-4">
        <Row className="g-4">
          <Col lg={6} className="order-2 order-lg-1">
            <Card className="border rounded-md mb-4">
              <Card.Header className="bg-white border-0 pt-4 px-4 ">
                <h5 className="fw-bold mb-1">
                  <i className="bi bi-gear-fill text-primary me-2"></i>
                  Setup Requirements
                </h5>
                <small className="text-muted">Ensure you meet these requirements before starting</small>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                {setupChecklist.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`d-flex align-items-start p-3 rounded-3 ${idx !== setupChecklist.length - 1 ? 'mb-2' : ''}`}
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <div className="me-3">
                      <i className={`bi ${item.icon} fs-4 text-primary`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <h6 className="mb-0 fw-semibold">{item.title}</h6>
                        {item.required && (
                          <Badge bg="danger" className="ms-2 px-2 py-1" style={{ fontSize: '0.65rem' }}>
                            Required
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">{item.desc}</small>
                    </div>
                  </div>
                ))}
              </Card.Body>
              <div className='text-end mb-2 px-3'>
                <Button
                  variant="link"
                  className="p-0 text-decoration-none small fw-semibold"
                  onClick={() => setShowHowItWorks(true)}
                >
                  How it works?
                </Button>
              </div>
            </Card>
          </Col>

          <Col lg={6} className="order-1 order-lg-2">
            <div className="d-flex align-items-center justify-content-center w-full">
              <Card className="w-100" >
                <Card.Body>
                  <div className='text-center p-1 border-bottom'>
                    <h5 className="fw-bold mb-1">
                      <i className="ri-p2p-line text-primary fs-4 me-2"></i>Interview Preparation
                    </h5>
                    <p className='text-muted'>{`AI will act as a ${interviewTypeLabel[formData.interviewType]}.`}</p>
                  </div>
                  <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3 mt-1 mb-4">
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Job Role <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Frontend Developer, Data Analyst"
                        value={formData.jobRole}
                        onChange={(e) => handleChange("jobRole", e.target.value)}
                        required
                      />
                    </Form.Group>

                    {(isenableTech == "technical" || isenableTech == "mixed") &&
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Tech Stack <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. reactjs, nextjs, python, java etc."
                          value={formData.techStack}
                          onChange={(e) => handleChange("techStack", e.target.value)}
                          required={(isenableTech == "technical" || isenableTech == "mixed") ? true : false}
                        />
                      </Form.Group>}

                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        Job Description <span className="text-muted">(Optional)</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        placeholder="Paste the job description here if available"
                        value={formData.jobDescription}
                        onChange={(e) => {
                          const jd = e.target.value;
                          const detectedType = detectInterviewType(jd);

                          setFormData(prev => ({
                            ...prev,
                            jobDescription: jd,
                            interviewType: detectedType
                          }));
                        }}
                      />
                      <div className="text-muted small text-end mt-1">
                        {formData.jobDescription.length}/500 characters
                      </div>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="fw-semibold">Interview Type</Form.Label>
                      <div className="d-flex justify-content-between mt-2">
                        {["technical", "hr", "mixed"].map((type) => (
                          <Form.Check
                            key={type}
                            type="radio"
                            name="interviewType"
                            label={type.toUpperCase()}
                            checked={formData.interviewType === type}
                            onChange={() => handleChange("interviewType", type)}
                          />
                        ))}
                      </div>
                    </Form.Group>

                    <div className="d-flex align-items-center justify-content-between">
                      <Form.Label className="fw-semibold mb-0">Select Duration</Form.Label>
                      <ButtonGroup>
                        {["10", "15", "20"].map((time) => (
                          <Button
                            key={time}
                            variant={formData.duration === time ? "primary" : "outline-primary"}
                            onClick={() => handleChange("duration", time)}
                          >
                            {time} min
                          </Button>
                        ))}
                      </ButtonGroup>
                    </div>

                    <Button
                      variant="primary"
                      size="lg"
                      className="w-100 py-2 fw-bold shadow-sm"
                      type="submit"
                      disabled={loader}
                    >
                      {loader ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Initializing Interview...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-mic-fill me-2"></i>
                          Start AI Interview
                        </>
                      )}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showHowItWorks}
        onHide={() => setShowHowItWorks(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
          <Modal.Title className="fw-bold">
            <i className="bi bi-info-circle-fill text-primary me-2"></i>
            How It Works
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4 pt-3">
          <Row className="g-4">
            {howItWorksSteps.map((item, idx) => (
              <Col md={6} key={idx}>
                <div className="h-100 p-3 rounded-3 border bg-light d-flex flex-column gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                      style={{ width: '28px', height: '28px', fontSize: '0.8rem' }}
                    >
                      {item.step}
                    </div>
                    <h6 className="fw-bold mb-0">{item.title}</h6>
                  </div>
                  <p className="text-muted small mb-0">{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="mt-4 text-center">
            <Button variant="primary" onClick={() => setShowHowItWorks(false)} className="px-4 fw-semibold">Got it, let's start!</Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default InterviewPrep;